// housing.js
// Provides Massachusetts housing issue configuration, including bills, groups, and stories.
import affordableHomesActImage from '../../assets/issue_images/ma/housing/affordable_homes_act.jpg';
import evictionMoratoriumImage from '../../assets/issue_images/ma/housing/eviction_moratorium.jpg';
import zoningMbtaMapImage from '../../assets/issue_images/ma/housing/zoning_MBTA_map.jpg';
import mphaStaircaseImage from '../../assets/issue_images/ma/housing/MPHA_staircase.jpg';

export const ISSUE_METADATA = {
  title: 'Housing advocacy in Massachusetts',
  summary:
    'Explore stories, bills, and advocacy groups working to make housing in Massachusetts more affordable and secure.',
};

export const STORY_SCROLL_CONTENT = {
  topic: 'housing',
  heroContent: {
    preGradientText: 'Everyday people are making housing',
    gradientText: 'more affordable',
    postGradientText: '',
    description: 'Learn how Massachusetts neighbors, renters, and advocates are shaping fairer housing and how you can join them.',
  },  
  lastUpdated: 'October 2025',
};

export const BILLS = [
  {
    id: 'rent-stabilization',
    title: 'Enabling cities and towns to stabilize rents and protect tenants',
    numbers: {
      house: { label: 'H2328', url: 'https://malegislature.gov/Bills/194/H2328' },
      senate: { label: 'S1447', url: 'https://malegislature.gov/Bills/194/S1447' },
    },
    short_summary: 'Caps rent increases; adds limits on evictions',
    short_summary_gerund: 'Capping rent increases; adding limits on evictions',
    why: 'Limits annual rent increases and adds just-cause eviction protections so renters can stay housed.',
    bullets: [
      'Allows cities to cap annual rent increases at 5% or CPI',
      'Requires just cause for evictions and non-renewals',
      'Exempts certain housing types',
    ],
    supporters: [
      {
        name: 'Homes for All Massachusetts',
        joinUrl: 'https://www.homesforallmass.org',
        infoUrl: 'https://www.homesforallmass.org/rentcontrol/',
      },
      {
        name: 'City Life/Vida Urbana',
        joinUrl: 'https://www.clvu.org',
        infoUrl: 'https://www.clvu.org/call_email_legislators_rent_stabilization',
      },
    ],
  },
  {
    id: 'topa',
    title: 'Tenant Opportunity to Purchase Act (TOPA)',
    numbers: {
      house: { label: 'H1544', url: 'https://malegislature.gov/Bills/194/H1544' },
      senate: { label: 'S998', url: 'https://malegislature.gov/Bills/194/S998' },
    },
    short_summary: 'Gives tenants first purchase right',
    short_summary_gerund: 'Giving tenants first purchase right',
    why: 'Gives tenants a first right to purchase their homes when a building is sold, supporting community stability and affordable housing.',
    bullets: [
      'Owners must notify tenants before sale',
      'Supports affordable housing initiatives',
    ],
    supporters: [
      {
        name: 'TOPA Coalition',
        joinUrl: 'https://www.topa4ma.org/endorsing-organizations.html',
        infoUrl: 'https://www.topa4ma.org/',
      },
    ],
  },
  {
    id: 'emergency-assistance',
    title: 'Improving emergency housing assistance for families experiencing homelessness',
    numbers: {
      house: { label: 'H216', url: 'https://malegislature.gov/Bills/194/H216' },
      senate: { label: 'S136', url: 'https://malegislature.gov/Bills/194/S136' },
    },
    short_summary: 'Streamlines emergency housing aid for families',
    short_summary_gerund: 'Streamlining emergency housing aid for families',
    why: 'Streamlines emergency assistance for families, including self-certification and an ombudsperson to resolve issues quickly.',
    bullets: [
      'Allows self-certification of urgent need',
      'Creates a housing ombudsperson for support/mediation',
    ],
    supporters: [
      {
        name: 'Massachusetts Coalition for the Homeless',
        joinUrl: 'https://www.mahomeless.org/current-bill-and-budget-priorities',
        infoUrl: 'https://www.mahomeless.org/current-bill-and-budget-priorities',
      },
    ],
  },
  {
    id: 'mrvp',
    title: 'Codifying the Massachusetts Rental Voucher Program (MRVP)',
    numbers: {
      house: { label: 'H1547', url: 'https://malegislature.gov/Bills/194/H1547' },
      senate: { label: 'S1008', url: 'https://malegislature.gov/Bills/194/S1008' },
    },
    short_summary: 'Codifies low-income rental vouchers',
    short_summary_gerund: 'Codifying low-income rental vouchers',
    why: 'Establishes MRVP in law to provide rental assistance, with most vouchers directed to very low-income households and quality inspections.',
    bullets: [
      'Rental assistance for low-income households',
      '75% of vouchers to very low-income families',
      'Quality inspections required before assistance',
    ],
    supporters: [
      {
        name: 'Abundant Housing Massachusetts',
        joinUrl: 'https://www.abundanthousingma.org/become-a-member/',
        infoUrl: 'https://www.abundanthousingma.org/policy/2023-24-priorities/',
      },
    ],
  },
  {
    id: 'crumbling-foundations',
    title: 'Relative to crumbling concrete foundations',
    numbers: {
      house: { label: 'H890', url: 'https://malegislature.gov/Bills/194/H890' },
      senate: { label: 'S580', url: 'https://malegislature.gov/Bills/194/S580' },
    },
    short_summary: 'Funds repairs for failing concrete foundations',
    short_summary_gerund: 'Funding repairs for failing concrete foundations',
    why: 'Creates a fund to help homeowners repair failing concrete foundations and exempts assistance from state taxes.',
    bullets: [
      'Creates Crumbling Concrete Assistance Fund',
      'Provides financial aid for repairs',
      'Exempts aid from state taxes',
    ],
    supporters: [
      {
        name: 'Massachusetts Residents Against Crumbling Foundations',
        joinUrl: 'https://www.massracf.com/',
        infoUrl: 'https://jenshenk.com/crumbling-concrete-foundations-in-massachusetts/',
      },
    ],
  },
];

export const BARRIER_STORY_CONTENT = [
  {
    key: 'make-a-difference',
    title: 'MA Affordable Homes Act 2024',
    imageUrl: affordableHomesActImage,
    imageSource: "mass.gov",
    imageAlt: 'Residents celebrating the Affordable Homes Act signing.',
    caption:
      'Grassroots coalitions worked for years and helped advance a $5.2B housing package, the largest in state history.',
    details: [
      'Tenant, faith, and public housing residents held rallies and meetings with Gov. Healey and legislators, repeatedly sharing firsthand stories of substandard housing',
      'Through advocacy organizations, everyday people worked for policies, such as the Supportive Housing Pool Fund and allowing ADUs, that were included in the final bill',
      "Hundreds of residents across Massachusetts lobbied their local legislators, submitted testimony, and met with housing committee staff to amplify the bill's community voice.",
    ],
    takeaway: 'Big change happens when grassroots organizations work together',
    sources: [
      'https://www.mass.gov/info-details/the-affordable-homes-act-smart-housing-livable-communities',
      'https://www.csh.org/2024/08/massachusetts-legislature-enacts-affordable-homes-act-ushering-in-a-new-era-for-supportive-housing',
      'https://www.mhp.net/news/2025/mhp-recognizes-housing-heroes-2025',
      'https://www.progressivemass.com/letter-to-the-conferees-on-the-affordable-homes-act',
      'https://www.abundanthousingma.org/abundant-housing-massachusetts-praises-the-massachusetts-legislature-for-passing-the-affordable-homes-act',
    ],
  },
  {
    key: 'emergency-eviction-moratorium',
    title: 'Emergency eviction moratorium',
    imageUrl: evictionMoratoriumImage,
    imageSource: "mapublichealth.org",
    imageAlt: 'Tenant organizers gathered together during the eviction moratorium campaign.',
    caption: 'During COVID, tenant organizers and advocates stopped most evictions statewide.',
    details: [
      "Over 150 groups signed a letter that became the template for the state's emergency eviction moratorium.",
      'Within weeks, evictions dropped by more than 90%.',
      'Tenant organizers made it happen. Groups like City Life/Vida Urbana provided template letters and coordinated a statewide “pledge of resistance,” ensuring tenants knew and used the new rights',
    ],
    takeaway: "One person's outreach can tip the balance",
  },
  {
    key: 'first-step',
    title: 'Local zoning reforms',
    imageUrl: zoningMbtaMapImage,
    imageSource: "mapc.org",
    imageAlt: 'Residents reviewing a zoning map together.',
    caption:
      'Dozens of towns passed zoning reforms after residents showed up — sometimes by just one vote.',
    details: [
      'The MBTA Communities law required towns to allow more homes near transit.',
      'Local residents joined groups like Arlington for Everyone or Brookline for Everyone to support change in their communities.',
      "Dozens of towns changed bylaws. To date, 119 MBTA communities have adopted compliant zoning.",
    ],
    takeaway: 'Advocacy groups navigate the complexity',
  },
  {
    key: 'people-like-you',
    title: 'Tenant advocates win counsel',
    imageUrl: mphaStaircaseImage,
    imageSource: "mapublichealth.org",
    imageAlt: 'Tenant advocates standing together on a staircase.',
    caption:
      'Tenants, parents, and retirees — not lobbyists — advanced the right to Housing Court and legal counsel.',
    details: [
      'Community members worked for $2.5 million to fund a right-to-counsel pilot providing legal representation to eligible tenants in eviction proceedings.',
      'Broad coalition backing: Over 240 organizations (including legal aid agencies, municipal leaders, faith groups, and others) joined the Access-to-Counsel coalition',
      "Tenant leaders and regular residents testified at hearings and meetings, directly shaping the program's design",
    ],
    takeaway: "You don't have to be a lobbyist to break through",
  },
];

export const GROUPS = [
  {
    name: 'Homes for All Massachusetts',
    joinUrl: 'https://www.homesforallmass.org',
    summary: 'Coalition fighting for rent control, anti-displacement, and housing rights statewide.',
    slug: 'homes-for-all-massachusetts',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['rent-stabilization', 'topa'],
  },
  {
    name: 'City Life/Vida Urbana',
    joinUrl: 'https://www.clvu.org',
    summary: 'Boston-based group supporting tenants facing eviction and advocating for housing justice.',
    slug: 'city-life-vida-urbana',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['rent-stabilization'],
  },
  {
    name: 'Massachusetts Residents Against Crumbling Foundations',
    joinUrl: 'https://www.massracf.com/',
    summary: 'Advocates for homeowners with failing foundations caused by pyrrhotite.',
    slug: 'mass-racf',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['crumbling-foundations'],
  },
  {
    name: 'Massachusetts Coalition for the Homeless',
    joinUrl: 'https://www.mahomeless.org/current-bill-and-budget-priorities',
    summary: 'Statewide nonprofit advocating for housing access and homelessness solutions.',
    slug: 'massachusetts-coalition-for-the-homeless',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['emergency-assistance'],
  },
  {
    name: 'Abundant Housing Massachusetts',
    joinUrl: 'https://www.abundanthousingma.org/takeactonyimbybill/',
    summary: 'Pro-housing group promoting inclusive policies and organizing for more homes.',
    slug: 'abundant-housing-massachusetts',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['mrvp'],
  },
  {
    name: 'TOPA Coalition',
    joinUrl: 'https://www.topa4ma.org/endorsing-organizations.html',
    summary: 'Coalition backing the Tenant Opportunity to Purchase Act to preserve affordable housing.',
    slug: 'topa-coalition',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['topa'],
  },
];
